// src/pages/Login/Login.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Login.css';
import Button from '../../components/Button';
import LoadingIndicator from '../../components/LoadingIndicator';
import { login as apiLogin } from '../../services/authService';
import { getSocialLoginUrl } from '../../services/socialLoginService';

const Login: React.FC = () => {
  const [activeMethod, setActiveMethod] = useState<'email' | 'line'>('email');
  const [loginStage, setLoginStage] = useState<'credentials' | 'otp'>(
    'credentials'
  );
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [otp, setOtp] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [notification, setNotification] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) navigate('/dashboard', { replace: true });
  }, [navigate]);

  // ステージ1: メール/パスワード認証
  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setNotification('');
    try {
      await apiLogin({ email, password });
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      if (detail === '2段階認証コードが必要です。') {
        setLoginStage('otp');
      } else {
        setNotification('メールアドレスまたはパスワードが正しくありません。');
      }
    } finally {
      setLoading(false);
    }
  };

  // ステージ2: OTP認証
  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setNotification('');
    try {
      await apiLogin({ email, password, code: otp });
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      if (detail === '無効な2段階認証コードです。') {
        setNotification('2段階認証コードが正しくありません。');
      } else {
        setNotification('ログインに失敗しました。再度お試しください。');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLineLogin = () => {
    const redirectUrl = getSocialLoginUrl('line');
    window.location.href = redirectUrl;
  };

  return (
    <div className="login-page">
      <h1 className="page-title">ログイン</h1>

      {loginStage === 'credentials' && (
        <div className="method-tabs">
          <button
            className={activeMethod === 'email' ? 'tab active' : 'tab'}
            onClick={() => setActiveMethod('email')}
          >
            メールでログイン
          </button>
          <button
            className={activeMethod === 'line' ? 'tab active' : 'tab'}
            onClick={() => setActiveMethod('line')}
          >
            LINEでログイン
          </button>
        </div>
      )}

      {loading && <LoadingIndicator isFullPage={false} />}
      {notification && <div className="notification">{notification}</div>}

      {loginStage === 'credentials' && activeMethod === 'email' && (
        <>
          <form className="login-form" onSubmit={handleCredentialsSubmit}>
            <div className="form-field">
              <label htmlFor="email">メールアドレス</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@example.com"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="password">パスワード</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="パスワード"
                required
              />
            </div>
            <Button type="submit" disabled={loading} width="100%">
              {loading ? '認証中...' : 'ログインする'}
            </Button>
          </form>
          <div className="auth-link">
            <p>
              アカウントをお持ちでない場合は <Link to="/register">こちら</Link>
            </p>
            <p>
              パスワードをお忘れの場合は{' '}
              <Link to="/password-reset">こちら</Link>
            </p>
          </div>
        </>
      )}

      {loginStage === 'credentials' && activeMethod === 'line' && (
        <>
          <div className="line-login">
            <p>LINEでログインします。以下のボタンをクリックしてください。</p>
            <Button onClick={handleLineLogin} disabled={loading} width="100%">
              {loading ? '処理中...' : 'LINEでログイン'}
            </Button>
          </div>
          <div className="auth-link">
            <p>
              アカウントをお持ちでない場合は <Link to="/register">こちら</Link>
            </p>
          </div>
        </>
      )}

      {loginStage === 'otp' && (
        <form className="login-form" onSubmit={handleOtpSubmit}>
          <h2 className="otp-title">2段階認証コードを入力</h2>
          <div className="form-field">
            <label htmlFor="otp">認証コード</label>
            <input
              type="text"
              id="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="6桁のコード"
              required
            />
          </div>
          <Button type="submit" disabled={loading}>
            {loading ? '認証中...' : '認証コードを送信'}
          </Button>
          <p className="otp-recovery">
            認証コードをお持ちでない場合は{' '}
            <Link to="/2fa/recovery">こちら</Link>
          </p>
        </form>
      )}
    </div>
  );
};

export default Login;
